/*
 * This file is part of glowingblue/gamilytics.
 *
 * Copyright (c) 2024 Glowing Blue AG.
 * Authors: Davide Iadeluca, Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import PaginatedListState from 'flarum/common/states/PaginatedListState';

// Settles never, like a Mithril-aborted request: a superseded load must not run the
// caller's `.finally`, which would clear the loading flags the current load still owns.
const superseded = () => new Promise(() => {});
export default class LeaderboardState extends PaginatedListState {
  get type() {
    return 'gamilytics_leaderboard';
  }
  get activityType() {
    return this.params?.activityType || null;
  }
  constructor(params, page, pageSize) {
    super(params, page, pageSize);
    this.activities = [];
    this.activitiesRequest = null;
    this.activitiesFailed = false;
    this.activitiesForbidden = false;
    this.resolving = false;
    this.resolveToken = 0;
    // Bumped by every refresh(); a page response from an older generation is discarded.
    this.generation = 0;
    // User id → score, captured as each current page lands; see scoreOf().
    this.scores = new Map();
    // The active activity type, or null when the actor may see no board.
    this.ready = Promise.resolve(null);
  }

  // Without this, the gap between mounting and resolveActivityType() settling reads as
  // an empty board rather than a loading one.
  isInitialLoading() {
    return this.resolving || super.isInitialLoading();
  }

  // `group` and `level` travel under `filter[]`: Flarum 2.x rejects any
  // all-lowercase query parameter outside the JSON:API allowlist.
  requestParams() {
    const {
      activityType,
      timeSpan,
      group,
      level
    } = super.requestParams();
    const params = {
      activityType,
      timeSpan: timeSpan || 'month'
    };
    if (group) {
      params.filter = {
        ...params.filter,
        group
      };
    }
    if (level) {
      params.filter = {
        ...params.filter,
        level
      };
    }
    return params;
  }
  stickyParams() {
    return {
      timeSpan: m.route.param('timeSpan'),
      group: m.route.param('group'),
      level: m.route.param('level')
    };
  }

  // The server lists exactly the boards the actor may open, exp first whenever it is
  // visible — so the first entry is the default board.
  refreshActivities() {
    this.activitiesRequest ??= app.store
    // A 403 renders the page's permission placeholder, which says it better than an alert.
    .find('gamilytics_activities', {
      filter: {
        leaderboard: true
      }
    }, undefined, {
      errorHandler: error => error.status === 403 ? undefined : false
    }).then(activities => {
      this.activities = activities;
      this.activitiesFailed = false;
      this.activitiesForbidden = false;
      m.redraw();
      return activities;
    }).catch(error => {
      // Dropping the cached promise lets the next visit retry instead of replaying the failure.
      this.activitiesRequest = null;
      this.activitiesFailed = true;
      this.activitiesForbidden = error?.status === 403;
      throw error;
    });
    return this.activitiesRequest;
  }

  // The cached list goes stale once an admin hides a board; the next resolve refetches it.
  invalidateActivities() {
    this.activitiesRequest = null;
  }

  /**
   * Resolves to `requested` when the actor may open it, to the first listed board when
   * nothing was requested, and to null otherwise — including when the list cannot load.
   */
  resolveActivityType(requested) {
    const token = ++this.resolveToken;
    this.resolving = true;
    this.ready = this.refreshActivities().then(activities => {
      const ids = activities.map(activity => activity.id());
      if (!requested) {
        return ids[0] ?? null;
      }
      return ids.includes(requested) ? requested : null;
    })
    // The store has already alerted; `activitiesFailed` renders the page's error state.
    .catch(() => null).finally(() => {
      // An older resolve settling late must not end a newer one's loading state.
      if (token === this.resolveToken) {
        this.resolving = false;
      }
      m.redraw();
    });
    return this.ready;
  }
  refresh(page) {
    if (page === void 0) {
      page = 1;
    }
    this.generation++;
    this.scores = new Map();
    // An in-flight revalidate() was just superseded and will never settle, so its
    // `.finally` cannot release the cached promise; left in place, every later call hangs.
    this.revalidating = null;
    return super.refresh(page);
  }

  // The list's users are store singletons whose `activitySummary` the next users payload
  // overwrites — a superseded board's late response included, since Store.find() pushes
  // before loadPage() can discard it. The copy taken when a current page landed is the
  // score this board actually returned.
  scoreOf(user) {
    return this.scores.has(user.id()) ? this.scores.get(user.id()) : user.activitySummary();
  }

  // Core's loadPage, plus the generation guard and an errorHandler: a superseded request
  // never alerts, and a 403 is left to the page, which refetches the board list instead.
  loadPage(page) {
    if (page === void 0) {
      page = 1;
    }
    const generation = this.generation;
    const isCurrent = () => generation === this.generation;
    const reqParams = this.requestParams();
    const include = Array.isArray(reqParams.include) ? reqParams.include.join(',') : reqParams.include;
    const params = {
      ...reqParams,
      page: {
        ...reqParams.page,
        offset: this.pageSize && this.pageSize * (page - 1) || 0,
        limit: this.pageSize || undefined
      },
      include
    };
    if (typeof params.include === 'undefined') {
      delete params.include;
    }
    return app.store.find(this.type, this.mutateRequestParams(params, page), undefined, {
      errorHandler: error => isCurrent() && error.status !== 403 ? false : undefined
    }).then(results => {
      if (!isCurrent()) {
        return superseded();
      }

      // Same microtask chain as the store's push, so no other payload can land in between.
      results.forEach(user => this.scores.set(user.id(), user.activitySummary()));
      const usedPerPage = results.payload?.meta?.perPage;
      const usedTotal = results.payload?.meta?.page?.total;
      if (!this.pageSize || usedPerPage && this.pageSize !== usedPerPage) {
        this.pageSize = usedPerPage || PaginatedListState.DEFAULT_PAGE_SIZE;
      }
      if (!this.totalItems || usedTotal && this.totalItems !== usedTotal) {
        this.totalItems = usedTotal || null;
      }
      return results;
    }, error => isCurrent() ? Promise.reject(error) : superseded());
  }
  defaultActivityType() {
    return this.activities[0]?.id() ?? null;
  }
  getPodium() {
    let podium = this.pages[0]?.items.slice(0, 3) || [];
    if (podium.length === 3 || podium.length === 2) {
      // The podium renders 2nd–1st–3rd, so the top two swap into display order.
      const second = podium[1];
      podium[1] = podium[0];
      podium[0] = second;
    }
    return podium;
  }
  getAllItems() {
    // All items of all pages except the podium
    return this.pages.reduce((items, page, i) => {
      if (i === 0) {
        return page.items.slice(3);
      }
      return [...items, ...page.items];
    }, []);
  }
  hasItems() {
    return this.getPodium().length > 0;
  }
}
flarum.reg.add('glowingblue-gamilytics', 'forum/leaderboards/states/LeaderboardState', LeaderboardState);